import { xmlEscape } from './xml-helpers';
import { withRelationship, withOverride } from './package-parts';

// ---------------------------------------------------------------------------
// Cross-document relationship resolution (see tagCrossRelIds in xml-helpers).
// ---------------------------------------------------------------------------

export const MEDIA_CONTENT_TYPES: Record<string, string> = {
  png: 'image/png', jpeg: 'image/jpeg', jpg: 'image/jpeg', gif: 'image/gif',
  bmp: 'image/bmp', tiff: 'image/tiff', tif: 'image/tiff', emf: 'image/x-emf',
  wmf: 'image/x-wmf', svg: 'image/svg+xml', ico: 'image/x-icon',
  bin: 'application/vnd.openxmlformats-officedocument.oleObject',
};

export function relsPathFor(partName: string): string {
  const i = partName.lastIndexOf('/');
  return partName.slice(0, i + 1) + '_rels/' + partName.slice(i + 1) + '.rels';
}

export function normalizeZipPath(p: string): string {
  const out: string[] = [];
  for (const seg of p.split('/')) {
    if (seg === '..') out.pop();
    else if (seg !== '.' && seg !== '') out.push(seg);
  }
  return out.join('/');
}

/** Span [start, endExclusive] of the innermost `<tag>...</tag>` containing idx. */
export function elementSpanAt(xml: string, idx: number, tag: string): [number, number] | null {
  const re = new RegExp(`<${tag}\\b[^>]*?>|</${tag}>`, 'g');
  const stack: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    if (m[0][1] === '/') {
      const s = stack.pop();
      if (s !== undefined && s <= idx && re.lastIndex > idx) return [s, re.lastIndex];
    } else if (!m[0].endsWith('/>')) {
      stack.push(m.index);
    }
    if (m.index > idx && stack.length === 0) break;
  }
  return null;
}

/** Remove/unwrap the construct that references an unresolvable FRO- id. */
export function stripFroConstruct(xml: string, srcId: string): string {
  const token = `"FRO-${srcId}"`;
  let guard = 0;
  let i: number;
  while ((i = xml.indexOf(token)) !== -1 && guard++ < 200) {
    const openStart = xml.lastIndexOf('<', i);
    const tagName = (xml.slice(openStart, i).match(/^<([\w:]+)/) || [])[1] || '';
    if (tagName === 'w:hyperlink') {
      const span = elementSpanAt(xml, i, 'w:hyperlink');
      if (span) {
        const openEnd = xml.indexOf('>', span[0]);
        const closeStart = span[1] - '</w:hyperlink>'.length;
        xml = xml.slice(0, span[0]) + xml.slice(openEnd + 1, closeStart) + xml.slice(span[1]);
        continue;
      }
      // self-closing hyperlink — just remove the element
      const selfEnd = xml.indexOf('>', i);
      xml = xml.slice(0, openStart) + xml.slice(selfEnd + 1);
      continue;
    }
    // an image/OLE reference — remove the whole enclosing drawing/pict/object
    let removed = false;
    for (const container of ['w:drawing', 'w:pict', 'w:object']) {
      const span = elementSpanAt(xml, i, container);
      if (span) { xml = xml.slice(0, span[0]) + xml.slice(span[1]); removed = true; break; }
    }
    if (removed) continue;
    // fallback: drop just the attribute (r:embed on a:blip is optional per schema)
    xml = xml.replace(new RegExp(`\\s(?:r:(?:id|embed|link)|o:relid)="FRO-${srcId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`), '');
  }
  return xml;
}


/**
 * Copy a part from the source package into the output, along with its own .rels
 * file and (recursively, depth-capped) the internal parts those rels point to —
 * a chart brings its colors/style/embedded-workbook parts, etc. Returns the
 * output path used (may be renamed on content collision), or null when the
 * source part is missing.
 */
export async function importPartWithDeps(newZip: any, srcZip: any, srcPath: string, depth: number): Promise<string | null> {
  const srcPart = srcZip && srcZip.file(srcPath);
  if (!srcPart) return null;
  const bytes: Uint8Array = await srcPart.async('uint8array');
  let outPath = srcPath;
  const existing = newZip.file(outPath);
  if (existing) {
    const cur: Uint8Array = await existing.async('uint8array');
    let same = cur.length === bytes.length;
    if (same) for (let k = 0; k < cur.length; k++) { if (cur[k] !== bytes[k]) { same = false; break; } }
    if (same) return outPath; // identical part already present — reuse
    const slash = srcPath.lastIndexOf('/');
    let n = 0;
    do {
      outPath = srcPath.slice(0, slash + 1) + `fro${n ? n : ''}_` + srcPath.slice(slash + 1);
      n++;
    } while (newZip.file(outPath) && n < 50);
  }
  newZip.file(outPath, bytes);
  // Bring the part's own relationships + their internal targets (depth-capped).
  if (depth < 3) {
    const srcRelsP = relsPathFor(srcPath);
    const relsFile = srcZip.file(srcRelsP);
    if (relsFile) {
      let relsXml: string = await relsFile.async('string');
      const dir = srcPath.slice(0, srcPath.lastIndexOf('/') + 1);
      for (const m of relsXml.matchAll(/<Relationship\b[^>]*>/g)) {
        if (/TargetMode="External"/.test(m[0])) continue;
        const t = (m[0].match(/ Target="([^"]*)"/) || [])[1];
        if (!t) continue;
        const depSrc = normalizeZipPath(dir + t.replace(/^\//, ''));
        const depOut = await importPartWithDeps(newZip, srcZip, depSrc, depth + 1);
        if (depOut && depOut !== depSrc) {
          // dependency was renamed — point this copy's rels at the new name
          const newTarget = depOut.startsWith(dir) ? depOut.slice(dir.length) : '/' + depOut;
          relsXml = relsXml.split(`Target="${t}"`).join(`Target="${xmlEscape(newTarget)}"`);
        }
      }
      newZip.file(relsPathFor(outPath), relsXml);
    }
    // content types for the dependencies + the part itself
    const ct0: string = (await newZip.file('[Content_Types].xml')?.async('string')) || '';
    const ct1 = await declareContentType(newZip, srcZip, srcPath, outPath, ct0);
    if (ct1 !== ct0) newZip.file('[Content_Types].xml', ct1);
  }
  return outPath;
}

/** Ensure the output [Content_Types].xml declares the copied part: prefer the
 *  source package's Override for the source path, else a Default by extension. */
export async function declareContentType(newZip: any, srcZip: any, srcPath: string, outPath: string, ctXml: string): Promise<string> {
  if (!ctXml) return ctXml;
  if (ctXml.includes(`PartName="/${outPath}"`)) return ctXml;
  const srcCt: string = srcZip ? ((await srcZip.file('[Content_Types].xml')?.async('string')) || '') : '';
  const ovm = srcCt.match(new RegExp(`<Override\\b[^>]*PartName="/${srcPath.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')}"[^>]*/>`));
  if (ovm) {
    const ctVal = (ovm[0].match(/ContentType="([^"]*)"/) || [])[1] || '';
    if (ctVal) return withOverride(ctXml, outPath, ctVal);
  }
  const ext = outPath.slice(outPath.lastIndexOf('.') + 1).toLowerCase();
  if (ext && ext !== outPath &&
      !new RegExp(`<Default\\b[^>]*\\bExtension="${ext}"`, 'i').test(ctXml)) {
    return ctXml.replace('</Types>',
      `<Default Extension="${ext}" ContentType="${MEDIA_CONTENT_TYPES[ext] || 'application/octet-stream'}"/></Types>`);
  }
  return ctXml;
}

/**
 * Resolve FRO- tagged relationship ids in an output part: import the relationship
 * (and internal target parts) from the source document's same-named part rels,
 * minting fresh non-colliding ids; strip what cannot be resolved.
 */
export async function resolveCrossRels(newZip: any, partName: string, srcZip: any): Promise<void> {
  const partFile = newZip.file(partName);
  if (!partFile) return;
  let xml: string = await partFile.async('string');
  if (xml.indexOf('"FRO-') === -1) return;

  const relsPath = relsPathFor(partName);
  const srcRelsXml: string = srcZip ? ((await srcZip.file(relsPath)?.async('string')) || '') : '';
  const srcRels = new Map<string, { type: string; target: string; mode: string }>();
  for (const m of srcRelsXml.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = (m[0].match(/ Id="([^"]*)"/) || [])[1];
    if (!id) continue;
    srcRels.set(id, {
      type: (m[0].match(/ Type="([^"]*)"/) || [])[1] || '',
      target: (m[0].match(/ Target="([^"]*)"/) || [])[1] || '',
      mode: (m[0].match(/ TargetMode="([^"]*)"/) || [])[1] || 'Internal',
    });
  }

  let outRelsXml: string = (await newZip.file(relsPath)?.async('string')) ||
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"></Relationships>';
  let ctXml: string = (await newZip.file('[Content_Types].xml')?.async('string')) || '';
  let relsChanged = false, ctChanged = false;

  const wanted = new Set<string>();
  for (const m of xml.matchAll(/"FRO-([^"]+)"/g)) wanted.add(m[1]);

  for (const srcId of wanted) {
    const rel = srcRels.get(srcId);
    if (!rel) { xml = stripFroConstruct(xml, srcId); continue; }

    let target = rel.target;
    if (rel.mode !== 'External') {
      const baseDir = partName.slice(0, partName.lastIndexOf('/') + 1);
      const srcPath = normalizeZipPath(baseDir + target.replace(/^\//, ''));
      const srcPart = srcZip && srcZip.file(srcPath);
      if (!srcPart) { xml = stripFroConstruct(xml, srcId); continue; }
      const outPath = await importPartWithDeps(newZip, srcZip, srcPath, 0);
      if (!outPath) { xml = stripFroConstruct(xml, srcId); continue; }
      // target expressed relative to this part's directory
      target = outPath.startsWith(baseDir) ? outPath.slice(baseDir.length) : '/' + outPath;
      // Declare a content type for the copied part: prefer the SOURCE's Override
      // (charts, smartart and other typed parts need it), else a Default by extension.
      const declared = await declareContentType(newZip, srcZip, srcPath, outPath, ctXml);
      if (declared !== ctXml) { ctXml = declared; ctChanged = true; }
    }

    const added = withRelationship(outRelsXml, { type: rel.type, target, external: rel.mode === 'External', prefix: 'rIdFRO' });
    outRelsXml = added.xml;
    const newId = added.id;
    relsChanged = true;
    xml = xml.split(`"FRO-${srcId}"`).join(`"${newId}"`);
  }

  // Defensive: no FRO- tag may ever reach the output.
  if (xml.indexOf('"FRO-') !== -1) {
    for (const m of [...xml.matchAll(/"FRO-([^"]+)"/g)]) xml = stripFroConstruct(xml, m[1]);
    xml = xml.replace(/\s(?:r:(?:id|embed|link)|o:relid)="FRO-[^"]*"/g, '');
  }

  newZip.file(partName, xml);
  if (relsChanged) newZip.file(relsPath, outRelsXml);
  if (ctChanged && ctXml) newZip.file('[Content_Types].xml', ctXml);
}
